/**
 * WeaveIn.ai Platform — Sycamores MCP Outreach Billing API client.
 *
 * Staff log outreach touches (SMS/phone with durations) + pre-enrollment
 * dispositions; the backend applies a per-payer billing rule and generates a
 * separate billing file per payer (CSV / HTML / X12 837P) plus matching claims.
 */

import axios, { AxiosInstance } from 'axios';

import { config as productConfig } from '@/src/config';

// ============================================================================
// TYPES
// ============================================================================

export interface OutreachTouch {
  id: number;
  channel: 'sms' | 'phone';
  minutes: number;
  date: string | null;
}

export interface OutreachConsumer {
  enrollment_id: number;
  member_id: number;
  name: string;
  member_number: string;
  payer_id: number | null;
  payer_name: string | null;
  prior_auth_number: string | null;
  pre_authorized: boolean;
  referral_id: string | null;
  disposition: string | null;
  enrollment_status: string | null;
  touches: OutreachTouch[];
}

export interface OutreachPreviewLine {
  enrollment_id: number;
  member_id: number;
  consumer_name: string;
  member_number: string;
  disposition: string | null;
  prior_auth_number: string | null;
  activity_count: number;
  included: boolean;
  exclusion_reason: string | null;
  billable_units: number;
  amount: number;
  touch_detail: Array<{
    channel: string;
    minutes: number;
    billed_minutes: number;
    units: number;
    amount: number;
    date: string | null;
  }>;
}

export interface OutreachPreviewPayer {
  payer_id: number;
  payer_name: string;
  rule_type: string;
  billing_activity_code: string;
  config: Record<string, unknown>;
  lines: OutreachPreviewLine[];
  consumer_count: number;
  activity_count: number;
  total_units: number;
  total_amount: number;
}

export interface OutreachPreview {
  period_start: string;
  period_end: string;
  payers: OutreachPreviewPayer[];
  grand_total: number;
}

export interface OutreachBillingRun {
  id: number;
  payer_name: string;
  rule_type: string;
  period_start: string | null;
  period_end: string | null;
  consumer_count: number;
  activity_count: number;
  total_units: number;
  total_amount: number;
  status: string;
  file_name: string;
  created_ts: string | null;
  approved_at?: string | null;
  submitted_at?: string | null;
  reviewer_notes?: string | null;
}

export interface OutreachRunResult {
  period_start: string;
  period_end: string;
  runs: Array<{
    run_id: number;
    payer_id: number;
    payer_name: string;
    rule_type: string;
    consumer_count: number;
    activity_count: number;
    total_units: number;
    total_amount: number;
    claim_count: number;
    file_name: string;
  }>;
  grand_total: number;
}

// ============================================================================
// CLIENT
// ============================================================================

class OutreachBillingApiService {
  private client: AxiosInstance;
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    this.client = axios.create({
      baseURL,
      headers: { 'Content-Type': 'application/json' },
    });
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      if (token) config.headers.Authorization = `Bearer ${token}`;
      if (cboId) config.headers['X-CBO-ID'] = cboId;
      return config;
    });
  }

  async getConsumers(): Promise<OutreachConsumer[]> {
    const { data } = await this.client.get('/outreach-billing/consumers');
    return data.consumers ?? [];
  }

  async logTouch(
    memberId: number,
    payload: { channel: 'sms' | 'phone'; minutes: number; date?: string },
  ): Promise<void> {
    await this.client.post(`/outreach-billing/${memberId}/touch`, payload);
  }

  async setDisposition(
    memberId: number,
    payload: { disposition: string | null; notes?: string },
  ): Promise<void> {
    await this.client.post(`/outreach-billing/${memberId}/disposition`, payload);
  }

  async loadSample(): Promise<{ consumers_loaded: number }> {
    const { data } = await this.client.post('/outreach-billing/load-sample');
    return data;
  }

  async resetDemo(): Promise<{ claims_removed: number; status: string }> {
    const { data } = await this.client.post('/outreach-billing/reset-demo');
    return data;
  }

  async preview(period = '2026-06'): Promise<OutreachPreview> {
    const { data } = await this.client.get('/outreach-billing/preview', {
      params: { period },
    });
    return data;
  }

  async getBillableActivities(period = '2026-06'): Promise<{
    period_start: string;
    period_end: string;
    activities: BillableActivity[];
    total_amount: number;
  }> {
    const { data } = await this.client.get('/outreach-billing/billable-activities', {
      params: { period },
    });
    return data;
  }

  async generateRuns(period = '2026-06'): Promise<OutreachRunResult> {
    const { data } = await this.client.post('/outreach-billing/runs', null, {
      params: { period },
    });
    return data;
  }

  async listRuns(): Promise<OutreachBillingRun[]> {
    const { data } = await this.client.get('/outreach-billing/runs');
    return data.runs ?? [];
  }

  /** Absolute URL (with auth/cbo query fallback not needed — download via fetch below). */
  fileUrl(runId: number, format: 'csv' | 'html' | '837'): string {
    return `${this.baseURL}/outreach-billing/runs/${runId}/file?format=${format}`;
  }

  /** Fetch a billing file with auth headers and return a blob (for download/preview). */
  async fetchFile(
    runId: number,
    format: 'csv' | 'html' | '837',
  ): Promise<{ blob: Blob; filename: string }> {
    const resp = await this.client.get(`/outreach-billing/runs/${runId}/file`, {
      params: { format },
      responseType: 'blob',
    });
    const disposition = resp.headers['content-disposition'] || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : `outreach_billing_${runId}.${format}`;
    return { blob: resp.data as Blob, filename };
  }

  // ----- staff encounter documentation -----
  async getTemplates(
    channel: 'sms' | 'email' | 'phone' = 'sms',
  ): Promise<Array<{ id: number; name: string; subject?: string | null; body: string; variable_fields: string[] | null }>> {
    const { data } = await this.client.get('/outreach-billing/sms-templates', { params: { channel } });
    return data.templates ?? [];
  }

  /** @deprecated use getTemplates('sms') */
  async getSmsTemplates() {
    return this.getTemplates('sms');
  }

  async getEncounter(memberId: number): Promise<OutreachEncounter> {
    const { data } = await this.client.get(`/outreach-billing/${memberId}/encounter`);
    return data;
  }

  /** Read-only traceability trail for one member (touches, notes, claims). */
  async getMemberHistory(memberId: number): Promise<OutreachMemberHistory> {
    const { data } = await this.client.get(`/outreach-billing/${memberId}/history`);
    return data;
  }

  async sendSms(
    memberId: number,
    payload: { template_id?: number; message?: string; minutes?: number; to_number?: string; engagement_type?: string },
  ): Promise<{ touch_id: number; sent: boolean; rendered_message: string; error: string | null }> {
    const { data } = await this.client.post(`/outreach-billing/${memberId}/send-sms`, payload);
    return data;
  }

  async sendEmail(
    memberId: number,
    payload: {
      template_id?: number;
      subject?: string;
      message?: string;
      minutes?: number;
      to_email?: string;
      engagement_type?: string;
    },
  ): Promise<{ touch_id: number; sent: boolean; rendered_message: string; subject: string; error: string | null }> {
    const { data } = await this.client.post(`/outreach-billing/${memberId}/send-email`, payload);
    return data;
  }

  async logCall(
    memberId: number,
    payload: { minutes: number; notes?: string; to_number?: string; initiate?: boolean; engagement_type?: string },
  ): Promise<{ touch_id: number; dialed: boolean; error: string | null }> {
    const { data } = await this.client.post(`/outreach-billing/${memberId}/log-call`, payload);
    return data;
  }

  async draftProgressNote(memberId: number): Promise<Record<string, any>> {
    const { data } = await this.client.post(`/outreach-billing/${memberId}/draft-progress-note`);
    return data;
  }

  async saveProgressNote(
    memberId: number,
    soap: Record<string, any>,
    encounterMeta?: Record<string, any>,
  ): Promise<{ form_instance_id: number; status: string }> {
    const { data } = await this.client.post(`/outreach-billing/${memberId}/save-progress-note`, {
      soap,
      encounter_meta: encounterMeta ?? {},
    });
    return data;
  }

  // ----- supervisor approval / submission -----
  async approveRun(runId: number, notes?: string): Promise<{ run_id: number; status: string }> {
    const { data } = await this.client.post(`/outreach-billing/runs/${runId}/approve`, { notes });
    return data;
  }

  async submitRun(
    runId: number,
  ): Promise<{ run_id: number; status: string; claims_submitted: number; claims_paid: number }> {
    const { data } = await this.client.post(`/outreach-billing/runs/${runId}/submit`, {});
    return data;
  }

  // ----- configurable engagement / disposition types (per program + MCP) -----
  async getTypeConfigs(): Promise<OutreachTypeConfig[]> {
    const { data } = await this.client.get('/outreach-billing/type-config');
    return data.configs ?? [];
  }

  async saveTypeConfig(payload: {
    program_id: number | null;
    payer_id: number | null;
    engagement_types: TypeOption[];
    disposition_types: TypeOption[];
  }): Promise<{ id: number; status: string }> {
    const { data } = await this.client.put('/outreach-billing/type-config', payload);
    return data;
  }
}

export interface TypeOption {
  value: string;
  label: string;
}

export interface OutreachTypeConfig {
  id: number;
  program_id: number | null;
  payer_id: number | null;
  program_name: string | null;
  payer_name: string | null;
  engagement_types: TypeOption[];
  disposition_types: TypeOption[];
}

export interface OutreachEncounter {
  member_id: number;
  enrollment_id: number;
  name: string;
  member_number: string;
  phone: string | null;
  email: string | null;
  payer_name: string | null;
  prior_auth_number: string | null;
  disposition: string | null;
  engagement_types: TypeOption[];
  disposition_types: TypeOption[];
  touches: Array<{ id: number; channel: string; minutes: number; notes: string; place_of_service?: string; date: string | null }>;
  saved_note: Record<string, any> | null;
}

export interface OutreachHistoryTouch {
  id: number;
  channel: string;
  minutes: number;
  notes: string;
  place_of_service: string;
  date: string | null;
}

export interface OutreachHistoryNote {
  id: number;
  status: string;
  soap: Record<string, any>;
  author: string | null;
  date: string | null;
}

export interface OutreachHistoryClaim {
  id: number;
  claim_number: string;
  payer_name: string | null;
  amount: number;
  status: string;
  service_from: string | null;
  service_to: string | null;
  date: string | null;
}

export interface OutreachMemberHistory {
  member_id: number;
  enrollment_id: number | null;
  touches: OutreachHistoryTouch[];
  notes: OutreachHistoryNote[];
  claims: OutreachHistoryClaim[];
  totals: {
    touch_count: number;
    note_count: number;
    claim_count: number;
    billed_amount: number;
  };
}

export interface BillableActivity {
  member_id: number;
  consumer_name: string;
  payer_name: string;
  channel: string;
  date: string | null;
  minutes: number | null;
  billed_minutes: number | null;
  units: number;
  amount: number;
  place_of_service: string;
  disposition: string | null;
  included: boolean;
  exclusion_reason: string | null;
}

const getApiBaseUrl = (): string =>
  (productConfig.weaveinApi.baseUrl ?? 'https://weavein-api.onrender.com').trim() + '/api';

export const outreachBillingApi = new OutreachBillingApiService(getApiBaseUrl());
export default outreachBillingApi;
